import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useStatsStore, useStatisticOptions } from '../store';
import { getStats } from '../utils/api';

import { useStatisticSorting } from './sorting';
import { useStatisticTypes } from './statTypes';

export const useStatistics = () => {
  const filters = useStatsStore((state) => state.filters);

  const { options } = useStatisticOptions();
  const { types } = useStatisticTypes();
  const { sorting, order } = useStatisticSorting();

  const sortedTypes = useMemo(() => {
    if (!sorting?.name || !types?.length) return [];
    return [sorting.name, ...types.filter((s) => s.name !== sorting.name).map((s) => s.name)];
  }, [sorting, types]);

  const {
    data: stats,
    isLoading,
    isFetched,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['statistics', options, filters, sortedTypes, order],
    queryFn: ({ pageParam, signal }) =>
      getStats(
        {
          endpoint: options.statType,
          stats: sortedTypes,
          order: order,
          phase: filters?.phase,
          seasonYear: options.season,
          teamId: filters?.teamId,
          limit: options.pageSize,
          offset: pageParam * options.pageSize,
        },
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage.length ? allPages.length : undefined),
    select: (data) => data?.pages.flatMap((page) => page) ?? [],
    enabled: !!sortedTypes.length,
  });

  return { stats: stats ?? [], loadMoreStats: fetchNextPage, isLoading, isFetched, isFetchingNextPage };
};
